import { useMemo, useState } from 'react'
import {
  PiArrowLeft,
  PiArrowRight,
  PiCaretDown,
  PiConfetti,
  PiHandshake,
  PiProhibit,
  PiProhibitInset,
  PiX,
} from 'react-icons/pi'
import { IconType } from 'react-icons'
import { cn } from 'components/UI/cn'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { SalesStage } from 'models/sales_stages'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import {
  getStageColor,
  isTerminalStage,
  sortStagesPipelineOrder,
} from './stageColors'

/** Compact labels for the terminal button group. */
const TERMINAL_BUTTON_LABELS: Record<string, string> = {
  'closed won': 'Won',
  'closed lost': 'Lost',
  churn: 'Churn',
}

/** Full-width terminal banner styles keyed by lowercase stage name. */
const TERMINAL_BANNER_STYLES: Record<string, { wrap: string; icon: IconType }> =
  {
    // Border colors are `!`-prefixed so they beat Bootstrap's `.border`
    // !important shorthand (which would otherwise force them to #dee2e6), and
    // use mid-tone hues to match the pipeline stage-bar stroke.
    'closed won': {
      wrap: 'bg-alert-success-bg text-alert-success-text !border-green-400',
      icon: PiConfetti,
    },
    'closed lost': {
      wrap: 'bg-alert-danger-bg text-alert-danger-text !border-red-400',
      icon: PiProhibit,
    },
    churn: {
      wrap: 'bg-alert-default-bg text-neutral-500 !border-neutral-400',
      icon: PiProhibitInset,
    },
  }

// ─── Sales Stage Bar ─────────────────────────────────────────────────────────

interface SalesStageBarProps {
  /** Full list of sales stages (sorting is handled internally). */
  stages: SalesStage[]
  /** Currently active stage ID. */
  activeStageId: number | null
  /** Called when the user selects a different stage. */
  onStageChange: (stageId: number) => void
  /**
   * Called to clear the stage back to "no stage yet". When provided, the back
   * cluster shows an "×" clear button in place of the otherwise-disabled caret
   * once the deal is at the first pipeline stage.
   */
  onClearStage?: () => void
}

/** A labeled section of stage options inside the terminal-banner dropdown. */
interface StageGroup {
  label: string
  items: SalesStage[]
}

/**
 * Single horizontal stage bar: a back-dropdown cluster, a phase-colored
 * current-stage fill, a forward-dropdown cluster, and the Won / Lost / Churn
 * terminal button group. When the deal is already in a terminal stage, the bar
 * becomes a full-width tinted banner with a single dropdown that offers the
 * other terminal outcomes plus the option to re-open into a pipeline stage.
 */
export function SalesStageBar({
  stages,
  activeStageId,
  onStageChange,
  onClearStage,
}: SalesStageBarProps) {
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')
  // Below this width the row can't fit the terminal Won/Lost/Churn group
  // without squeezing the current stage name, which matters more — so the
  // group collapses into the "Move forward" caret menu instead. Tune the
  // query to shift where the handoff happens.
  const isCompact = useMediaQuery('(max-width: 1279px)')

  if (stages.length === 0) return null

  const sorted = sortStagesPipelineOrder(stages, customOrderEnabled)
  const regularStages = sorted.filter((s) => !isTerminalStage(s))
  const terminalStages = sorted.filter((s) => isTerminalStage(s))

  const currentStage = sorted.find((s) => s.id === activeStageId) ?? null
  const currentIsTerminal = currentStage ? isTerminalStage(currentStage) : false

  // ── Terminal banner mode ──
  if (currentStage && currentIsTerminal) {
    // Offer the OTHER terminal outcomes first (e.g. Closed Won → Closed Lost +
    // Churn; Churn → both Closeds), then the option to re-open into a stage.
    const otherTerminals = terminalStages.filter(
      (s) => s.id !== currentStage.id
    )
    return (
      <TerminalBanner
        stage={currentStage}
        groups={[
          { label: 'Change outcome', items: otherTerminals },
          { label: 'Re-open to a stage', items: regularStages },
        ]}
        stages={sorted}
        customOrderEnabled={customOrderEnabled}
        onSelect={onStageChange}
        onClear={onClearStage}
      />
    )
  }

  // ── Regular pipeline mode ──
  const regularIndex = regularStages.findIndex((s) => s.id === activeStageId)

  // No stage yet (regularIndex === -1): there's nothing to step "back" from, so
  // "Move forward" advances into the first pipeline stage and the forward menu
  // surfaces every stage — otherwise the user is stuck with disabled carets.
  const noStageSelected = regularIndex === -1
  const prevStage = regularIndex > 0 ? regularStages[regularIndex - 1] : null
  const nextStage = noStageSelected
    ? (regularStages[0] ?? null)
    : regularIndex < regularStages.length - 1
      ? regularStages[regularIndex + 1]
      : null

  const earlierOptions =
    regularIndex > 0 ? regularStages.slice(0, regularIndex) : []
  const laterOptions = noStageSelected
    ? regularStages
    : regularStages.slice(regularIndex + 1)

  const fillTone = currentStage
    ? getStageColor(currentStage, sorted, customOrderEnabled)
    : null
  // Buckets are gone, so every non-terminal stage shares one pipeline icon.
  // (Terminal stages render the banner above, not this fill.)
  const FillIcon = currentStage ? PiHandshake : null

  return (
    <div className="flex items-center gap-8">
      {/* Steps: back cluster + fill + forward cluster */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <BackCluster
          prevStage={prevStage}
          earlierOptions={earlierOptions}
          stages={sorted}
          customOrderEnabled={customOrderEnabled}
          onSelect={onStageChange}
          onClear={currentStage ? onClearStage : undefined}
        />

        {/* Current stage fill bar — phase color, stage name as identity */}
        <div
          className={cn(
            'flex h-8 min-w-0 flex-1 items-center justify-center gap-2 rounded-md border px-2 transition-colors',
            fillTone
              ? cn(fillTone.bg, fillTone.text, fillTone.stroke)
              : 'border-gray-200 bg-gray-100 text-gray-500'
          )}
        >
          {FillIcon && <FillIcon size={12} />}
          <span className="truncate text-sm font-medium tracking-[-0.2px]">
            {currentStage?.stage ?? 'No stage yet'}
          </span>
        </div>

        <ForwardCluster
          nextStage={nextStage}
          laterOptions={laterOptions}
          stages={sorted}
          customOrderEnabled={customOrderEnabled}
          onSelect={onStageChange}
          menuAriaLabel={
            noStageSelected ? 'Choose a stage' : 'Jump to a later stage'
          }
          // When the row is too tight for the terminal button group, surface
          // those outcomes inside the forward menu instead so they stay
          // reachable (and the menu stays enabled at the last stage).
          terminalOptions={isCompact ? terminalStages : undefined}
        />
      </div>

      {/* Terminal stage button group — hidden when compact; its outcomes move
          into the forward caret menu (see ForwardCluster terminalOptions). */}
      {terminalStages.length > 0 && !isCompact && (
        <div className="ml-auto flex h-8 flex-shrink-0 items-center overflow-hidden rounded-md border border-gray-300 bg-white">
          {terminalStages.map((stage, i) => (
            <button
              key={stage.id}
              type="button"
              onClick={() => onStageChange(stage.id)}
              className={cn(
                'h-full px-3.5 text-sm font-medium tracking-[-0.2px] text-gray-800 transition-colors hover:bg-gray-100',
                i > 0 && 'border-l border-gray-300'
              )}
            >
              {TERMINAL_BUTTON_LABELS[stage.stage.toLowerCase()] ?? stage.stage}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Back / forward clusters ─────────────────────────────────────────────────

interface ClusterProps {
  stages: SalesStage[]
  customOrderEnabled: boolean
  onSelect: (stageId: number) => void
}

function BackCluster({
  prevStage,
  earlierOptions,
  stages,
  customOrderEnabled,
  onSelect,
  onClear,
}: ClusterProps & {
  prevStage: SalesStage | null
  earlierOptions: SalesStage[]
  onClear?: () => void
}) {
  const hasPrev = prevStage != null

  // At the first pipeline stage there's nothing earlier to step back to, so the
  // caret would be permanently disabled. Offer a clear ("×") affordance there
  // instead, letting the user step the deal back to the "No stage yet" state.
  if (onClear && !hasPrev && earlierOptions.length === 0) {
    // Tooltip wraps the bordered div (which has an explicit h-8) rather than the
    // button — the tooltip's inline-flex wrapper has no height, so a wrapped
    // `h-full` button would collapse. The div keeps the border + white bg; the
    // transparent button fills it and grays on hover.
    return (
      <FloatingTooltip theme="dark" text="Clear stage">
        <div className="flex h-8 flex-shrink-0 items-center rounded-md border border-gray-300 bg-white">
          <button
            type="button"
            aria-label="Clear stage"
            onClick={onClear}
            className="flex h-full items-center justify-center rounded-md px-2.5 text-gray-800 transition-colors hover:bg-gray-100"
          >
            <PiX size={14} />
          </button>
        </div>
      </FloatingTooltip>
    )
  }

  return (
    <div className="flex h-8 flex-shrink-0 items-center divide-x divide-gray-300 rounded-md border border-gray-300 bg-white">
      <CaretMenu
        options={earlierOptions}
        stages={stages}
        customOrderEnabled={customOrderEnabled}
        onSelect={onSelect}
        onClear={onClear}
        align="left"
        roundLeft
        roundRight={!hasPrev}
        ariaLabel="Jump to an earlier stage"
      />
      {hasPrev && (
        <QuickStageButton
          direction="prev"
          roundRight
          onClick={() => onSelect(prevStage.id)}
        />
      )}
    </div>
  )
}

function ForwardCluster({
  nextStage,
  laterOptions,
  stages,
  customOrderEnabled,
  onSelect,
  menuAriaLabel,
  terminalOptions,
}: ClusterProps & {
  nextStage: SalesStage | null
  laterOptions: SalesStage[]
  menuAriaLabel: string
  /** Terminal outcomes folded into the menu when the button group is hidden. */
  terminalOptions?: SalesStage[]
}) {
  const hasNext = nextStage != null
  const groups: StageGroup[] | undefined =
    terminalOptions && terminalOptions.length > 0
      ? [{ label: 'Close out', items: terminalOptions }]
      : undefined
  return (
    <div className="flex h-8 flex-shrink-0 items-center divide-x divide-gray-300 rounded-md border border-gray-300 bg-white">
      {hasNext && (
        <QuickStageButton
          direction="next"
          roundLeft
          onClick={() => onSelect(nextStage.id)}
        />
      )}
      <CaretMenu
        options={laterOptions}
        groups={groups}
        stages={stages}
        customOrderEnabled={customOrderEnabled}
        onSelect={onSelect}
        align="right"
        roundRight
        roundLeft={!hasNext}
        ariaLabel={menuAriaLabel}
      />
    </div>
  )
}

// ─── Quick prev/next button (one segment of a cluster) ───────────────────────

function QuickStageButton({
  direction,
  roundLeft,
  roundRight,
  onClick,
}: {
  direction: 'prev' | 'next'
  roundLeft?: boolean
  roundRight?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-full items-center gap-1 px-2.5 text-sm font-medium tracking-[-0.2px] text-gray-800 transition-colors hover:bg-gray-100',
        roundLeft && 'rounded-l-md',
        roundRight && 'rounded-r-md'
      )}
    >
      {direction === 'prev' && <PiArrowLeft size={12} />}
      {direction === 'next' ? 'Move forward' : 'Move backward'}
      {direction === 'next' && <PiArrowRight size={12} />}
    </button>
  )
}

// ─── Caret dropdown menu (one segment of a cluster) ──────────────────────────

interface CaretMenuProps extends ClusterProps {
  /** Flat list of stage options (used by the back/forward clusters). */
  options?: SalesStage[]
  /** Labeled groups of options (used by the terminal banner). */
  groups?: StageGroup[]
  /**
   * When provided, the menu appends a "Clear stage" item that steps the deal
   * back to the "No stage yet" state. Wired into the back caret + terminal
   * banner so clearing is reachable from any stage — not just the first one
   * (where the back cluster shows a standalone "×" instead of a caret).
   */
  onClear?: () => void
  align: 'left' | 'right'
  roundLeft?: boolean
  roundRight?: boolean
  ariaLabel: string
}

function CaretMenu({
  options,
  groups,
  stages,
  customOrderEnabled,
  onSelect,
  onClear,
  align,
  roundLeft,
  roundRight,
  ariaLabel,
}: CaretMenuProps) {
  const [open, setOpen] = useState(false)
  const modalDepth = useModalDepth()
  const zIndex = useMemo(() => getDropdownZIndex(modalDepth), [modalDepth])

  const visibleGroups = (groups ?? []).filter((g) => g.items.length > 0)
  const optionCount = options?.length ?? 0
  const groupCount = visibleGroups.reduce((n, g) => n + g.items.length, 0)
  // The clear item alone is enough to keep the menu actionable, so a menu with
  // no stage options but an onClear handler is still enabled.
  const disabled = optionCount + groupCount === 0 && !onClear

  const renderItem = (stage: SalesStage) => {
    const dot = getStageColor(stage, stages, customOrderEnabled).dot
    return (
      <button
        key={stage.id}
        type="button"
        role="menuitem"
        onClick={() => {
          onSelect(stage.id)
          setOpen(false)
        }}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-fg transition-colors hover:bg-neutral-100"
      >
        <span className={cn('size-3 flex-shrink-0 rounded-full', dot)} />
        {stage.stage}
      </button>
    )
  }

  return (
    <div className="relative h-full">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-full items-center justify-center px-2.5 text-gray-800 transition-colors',
          roundLeft && 'rounded-l-md',
          roundRight && 'rounded-r-md',
          disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-gray-100'
        )}
      >
        <PiCaretDown size={12} />
      </button>

      {open && !disabled && (
        <>
          {/* click-away backdrop (no useEffect needed) */}
          <div
            className="fixed inset-0"
            style={{ zIndex: zIndex - 1 }}
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            role="menu"
            style={{ zIndex }}
            className={cn(
              'absolute top-full mt-1 max-h-72 w-60 overflow-y-auto rounded-md border border-line bg-surface py-1 shadow-lg',
              align === 'right' ? 'right-0' : 'left-0'
            )}
          >
            {options?.map(renderItem)}
            {visibleGroups.map((group, gi) => {
              const needsDivider = gi > 0 || optionCount > 0
              return (
                <div key={group.label}>
                  <p
                    className={cn(
                      'px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-fg-tertiary',
                      needsDivider && 'mt-1 border-t border-line'
                    )}
                  >
                    {group.label}
                  </p>
                  {group.items.map(renderItem)}
                </div>
              )
            })}
            {onClear && (
              <>
                {optionCount + groupCount > 0 && (
                  <div className="my-1 border-t border-line" />
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onClear()
                    setOpen(false)
                  }}
                  className="flex w-full items-center gap-2 whitespace-nowrap px-3 py-2 text-left text-sm text-fg-secondary transition-colors hover:bg-neutral-100"
                >
                  <PiX size={12} className="flex-shrink-0" />
                  Clear stage
                  <span className="text-fg-tertiary">— No stage yet</span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Terminal banner ─────────────────────────────────────────────────────────

function TerminalBanner({
  stage,
  groups,
  stages,
  customOrderEnabled,
  onSelect,
  onClear,
}: {
  stage: SalesStage
  groups: StageGroup[]
  onClear?: () => void
} & ClusterProps) {
  const style =
    TERMINAL_BANNER_STYLES[stage.stage.toLowerCase()] ??
    TERMINAL_BANNER_STYLES.churn
  const Icon = style.icon

  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          'flex h-8 flex-1 items-center justify-center gap-2 rounded border',
          style.wrap
        )}
      >
        <Icon size={14} />
        <span className="text-sm font-medium tracking-[-0.2px]">
          {stage.stage}
        </span>
      </div>
      <div className="flex h-8 flex-shrink-0 items-center rounded-md border border-gray-300 bg-white">
        <CaretMenu
          groups={groups}
          stages={stages}
          customOrderEnabled={customOrderEnabled}
          onSelect={onSelect}
          onClear={onClear}
          align="right"
          roundLeft
          roundRight
          ariaLabel="Move out of this stage"
        />
      </div>
    </div>
  )
}
